import { Collapse, Divider, Space, Typography } from 'antd';
import { groupBy, isEmpty, sortBy } from 'lodash';
import {
  PersonalizationItemState,
  SpecialRequestState,
  useGetPersonalizationItemsQuery,
  useGetSpecialRequestsQuery,
  useUpdatePersonalizationItemMutation,
} from '@/redux';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { array } from '@/utils';
import { CommunityPermissions, PersonalizationItemDto } from '@/core';
import { ColorMarker, notify } from '@/components';
import { PersonalizationListItem } from './PersonalizationListItem';
import styles from './PersonalizationListItem.module.scss';
import { SpecialRequestListItem } from './SpecialRequest';
import { useCommunityContext } from '../Communities.Common';
import { PersonalizationItemFormValue } from './Item.Details';

const { Panel } = Collapse;
const { Text } = Typography;

interface ToggleSwitchProps {
  expanded: boolean;
  onToggleSwitch: () => void;
  setItemGroups: (value: any[]) => void;
  setOpenItems: (value: string[]) => void;
  specialRequestsKey: string;
  openItems: string[];
}

interface Props extends ToggleSwitchProps {
  homeId: string;
  projectId: string;
  stepId?: string;
  roomId?: string;
  readonly?: boolean;
  onDelete: (id: string) => any;
  compact?: boolean;
}

function useItemGroupsData(props: Props) {
  const { homeId, projectId, stepId, roomId } = props;

  const { data: items = array.empty<PersonalizationItemDto>() } =
    useGetPersonalizationItemsQuery({
      homeId,
      projectId,
      stepId,
      roomId,
      isFloorplan: true,
    });

  const grouped = groupBy(items, (x) => x.category.root.id);
  const itemGroups = Object.keys(grouped).map((id) => ({
    id,
    name: grouped[id][0].category.root.name,
    items: grouped[id],
    hexColor: grouped[id][0].category.root.hexColor,
  }));

  return sortBy(itemGroups, (x) => x.name);
}

const useUpdate = () => {
  const [update] = useUpdatePersonalizationItemMutation();

  return (args: PersonalizationItemFormValue, item: PersonalizationItemState) =>
    update({ ...args, id: item!.id })
      .unwrap()
      .then(() =>
        notify.success.t('personalization.item.details.detailsSaved'),
      );
};

export function useExpandItemsToggleSwitch(homeId: string): ToggleSwitchProps {
  const specialRequestsKey = 'special-requests' + homeId;
  const [itemGroups, setItemGroups] = useState<any[]>([]);
  const [openItems, setOpenItems] = useState<string[]>([]);
  const [expanded, setExpanded] = useState<boolean>(false);

  useEffect(() => {
    setExpanded(openItems.length > 0);
  }, [openItems.length]);

  return useMemo(() => {
    function onToggleSwitch() {
      const openItemsKeys = !expanded
        ? [...itemGroups.map((x) => x.id), specialRequestsKey]
        : [];
      setOpenItems(openItemsKeys);
    }

    return {
      expanded,
      onToggleSwitch,
      setItemGroups,
      setOpenItems,
      specialRequestsKey,
      openItems,
    };
  }, [expanded, itemGroups, openItems, specialRequestsKey]);
}

export function PersonalizationList(props: Props) {
  const {
    readonly,
    onDelete,
    homeId,
    projectId,
    stepId,
    compact,
    setOpenItems,
    openItems,
    specialRequestsKey,
  } = props;
  const { t } = useTranslation(undefined, {
    keyPrefix: 'personalization.item.details',
  });
  const itemGroups = useItemGroupsData(props);
  const { data: specialRequests = array.empty<SpecialRequestState>() } =
    useGetSpecialRequestsQuery({
      projectId,
      homeId,
      stepId,
      isFloorplan: true,
    });

  const handleSave = useUpdate();

  const onChange = (key: string | string[]) => {
    setOpenItems(key as string[]);
  };

  const { assert } = useCommunityContext();

  if (isEmpty(itemGroups) && isEmpty(specialRequests)) {
    return null;
  }

  return (
    <Collapse
      ghost
      activeKey={openItems}
      onChange={onChange}
      className={styles.cardHeader}
    >
      {itemGroups.map(({ id, name, items, hexColor }) => (
        <Panel
          key={id}
          header={
            <Space>
              <Text strong>{name}:</Text>
              <Text type="secondary">
                {t('subheading', { count: items.length })}
              </Text>
              <ColorMarker color={hexColor} />
            </Space>
          }
        >
          {items.map((item, index) => (
            <>
              <PersonalizationListItem
                readonly={
                  readonly ||
                  !assert(CommunityPermissions.Personalization.Manage, true)
                }
                item={item}
                key={item.id}
                className="mb-4"
                onDelete={onDelete}
                onSubmit={handleSave}
                compact={compact}
              />
              {index !== items.length - 1 && <Divider />}
            </>
          ))}
        </Panel>
      ))}
      {!isEmpty(specialRequests) && (
        <Panel
          key={specialRequestsKey}
          header={
            <Space>
              <Text strong>{t('specialRequests')}:</Text>
              <Text type="secondary">
                {t('subheading', {
                  count: specialRequests.length,
                })}
              </Text>
            </Space>
          }
        >
          {specialRequests.map((item, index) => (
            <div key={item.id}>
              <SpecialRequestListItem
                item={item}
                className="mb-4"
                readonly={
                  readonly ||
                  !assert(
                    CommunityPermissions.Personalization.SpecialRequestComment,
                    true,
                  )
                }
                compact={compact}
              />
              {index !== specialRequests.length - 1 && <Divider />}
            </div>
          ))}
        </Panel>
      )}
    </Collapse>
  );
}
